<!--
  SearchPage.vue -- the storefront search RESULTS page.

  Card: Storefront browse (https://trello.com/c/HUIrLhaU), pebble "Search".

  Jake, 2026-08-29: "I want a search result page with all matching products
  shown as cards, and the title of the page and breadcrumb should be
  'Search: [SEARCH_TERMS]' where SEARCH_TERMS is the text I typed (sanitized
  obviously)."

  At /shop/<storeSlug>/search?q=<terms>. Mounted inside StorefrontLayout via
  meta.layout='storefront', under the SAME StorefrontSessionGate as Home /
  Category / Product so the shopper session and the password gate apply
  identically -- search must not be a way around either.

  No breadcrumb: the layout's pinned nav carries Home and the h1 is the title
  (Bug Intake #456 convention; the Product page keeps its trail because it
  carries the category).

  Layout mirrors CategoryPage so navigating between them does not jump:

    <h1>                  ("Search: <terms>")
    StorefrontProductGrid (matches) | empty state
    StorefrontFooter

  THE TERM IS REFLECTED USER INPUT. It reaches this page from the URL and is
  rendered in the H1, which makes it a
  textbook reflected-XSS sink. It renders through `{{ }}` interpolation,
  which escapes; it may NEVER become a v-html sink. `displayTerm` additionally
  ELIDES a long term -- that cap is a LAYOUT guard (an unbroken 400-char
  string would otherwise stretch the header), NOT the security control. The
  escaping is the security control, and it is pinned by
  `storefront-search-page.spec.js`, which asserts an injection payload
  renders as literal text AND creates no element.

  WHY THE QUERY PARAM (not a path segment): search terms contain spaces and
  slashes, both of which fight a path segment; `?q=` keeps the URL
  shareable and is what the browser's own encoding rules already handle.

  ZERO MATCHES IS A VALID ANSWER, not a 404: the page renders in full with
  its title and an empty state, because "nothing matches X" is information
  the shopper asked for. A 404 would also make the store look broken.
-->
<template>
  <div class="sf-search-page" data-testid="sf-search-page">

    <div class="sf-search-page__body sf-container">
      <!-- SECURITY: text interpolation, never v-html. See the header note. -->
      <h1 class="sf-search-page__title" data-testid="sf-search-title">
        Search: {{ displayTerm }}
      </h1>

      <div
        v-if="loading"
        class="sf-search-page__loading"
        data-testid="sf-search-loading"
      >
        Loading. Please wait...
      </div>

      <div
        v-else-if="error"
        class="sf-search-page__error"
        data-testid="sf-search-error"
      >
        {{ error.message }}
      </div>

      <template v-else>
        <p
          v-if="products.length > 0"
          class="sf-search-page__count"
          data-testid="sf-search-count"
        >
          {{ countLabel }}
        </p>

        <storefront-product-grid
          v-if="products.length > 0"
          :products="products"
          :store-slug="slug"
          category-slug=""
        />

        <div
          v-else
          class="sf-search-page__empty"
          data-testid="sf-search-empty"
        >
          No products match your search. Try a different word or style number.
        </div>
      </template>
    </div>

    <storefront-footer
      :policies="policies"
      :store-slug="slug"
      :white-label="whiteLabel"
    />
  </div>
</template>

<script>
import { resolveStorefrontSlug } from "@/router/storefrontSlug";
import { useStorefrontChrome } from "@/composables/useStorefrontChrome";
import { useStorefrontSearch } from "@/composables/useStorefrontSearch";

import StorefrontProductGrid from "@/components/Storefront/StorefrontProductGrid.vue";
import StorefrontFooter from "@/components/Storefront/StorefrontFooter.vue";

// How much of the term is DISPLAYED in the title. A layout
// guard, not a security one (the escaping is the security control): an
// unbroken 400-character term would otherwise stretch the header and the
// crumb trail. The server caps what it will SEARCH at 120 separately.
const DISPLAY_TERM_MAX = 80;

export default {
  name: "StorefrontSearchPage",

  components: {
    "storefront-product-grid": StorefrontProductGrid,
    "storefront-footer": StorefrontFooter,
  },

  setup() {
    const {
      fetchChrome,
      policies,
      // White-label branding -> the footer's brand mark
      // (card https://trello.com/c/mq26K91V).
      whiteLabel,
    } = useStorefrontChrome();

    const { products, total, loading, error, search } = useStorefrontSearch();

    return {
      fetchChrome,
      policies,
      whiteLabel,
      products,
      total,
      loading,
      error,
      search,
    };
  },

  computed: {
    slug() {
      return resolveStorefrontSlug(this.$route) || "";
    },

    // The raw term from the URL. Rendered only through `displayTerm`, and
    // only ever as escaped text.
    searchTerm() {
      const q = this.$route?.query?.q;
      if (Array.isArray(q)) return q[0] || "";
      return typeof q === "string" ? q : "";
    },

    // Elided for display. Escaping (not truncation) is what makes this
    // safe; the cap only protects the layout.
    displayTerm() {
      const t = this.searchTerm.trim();
      return t.length > DISPLAY_TERM_MAX
        ? `${t.slice(0, DISPLAY_TERM_MAX)}...`
        : t;
    },

    countLabel() {
      const n = this.total;
      return n === 1 ? "1 product" : `${n} products`;
    },

    // Home / "Search: <terms>". The terminal crumb carries no path (it is
    // the current page), matching CategoryPage's trail shape.
  },

  watch: {
    searchTerm: {
      immediate: true,
      handler() {
        this.loadAll();
      },
    },
  },

  methods: {
    loadAll() {
      if (!this.slug) return;
      this.fetchChrome(this.slug);
      this.search(this.slug, this.searchTerm);
    },
  },
};
</script>

<style scoped>
/* Sticky footer, same contract as CategoryPage -- see its style block for
   why this is min-height:100% rather than 100dvh (StorefrontLayout is a
   fixed-viewport shell whose main element is the scroll container). */
.sf-search-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.sf-search-page > .sf-footer {
  margin-top: auto;
}

.sf-search-page__body {
  padding: 8px 32px 48px;
}

.sf-search-page__title {
  margin: 16px 0 4px;
  font-size: 24px;
  font-weight: 600;
  color: var(--tf-fg-1, #212121);
  /* A long term is elided in `displayTerm`; this is the second guard so a
     single unbroken string still cannot overflow the header. */
  overflow-wrap: anywhere;
}

.sf-search-page__count {
  margin: 0 0 20px;
  font-size: 14px;
  color: var(--tf-fg-3, #616161);
}

.sf-search-page__loading,
.sf-search-page__error,
.sf-search-page__empty {
  padding: 32px;
  text-align: center;
  color: var(--tf-fg-3, #616161);
}
</style>
